/**
 * Product Information Animation
 * Animates product image on page load, then follows scroll into carousel
 */

class ProductInformationAnimation {
  constructor(container) {
    this.container = container;
    this.productImage = container.querySelector('.product-media__image');

    this.hasStartedScrollAnimation = false;
    this.hasCompletedAnimation = false;

    this.startPosition = null;
    this.targetPosition = null;
    this.scrollStartY = null;
    this.scrollDistance = null;

    this.targetCarouselSlide = null;
    this.targetCarouselImage = null;
    this.spacerElement = null;
    this.scrollListener = null;
    this.lastLoggedProgress = null;
  }

  init() {
    if (!this.shouldAnimate()) {
      this.container.setAttribute('data-animation-phase', 'visible');
      return;
    }

    this.disableCarousel();
    // Initial state: slightly off-screen top
    this.container.setAttribute('data-animation-phase', 'initial');

    // Animate into view
    setTimeout(() => {
      this.container.setAttribute('data-animation-phase', 'visible');

      // Setup scroll tracking after page load animation completes
      setTimeout(() => {
        this.setupScrollTracking();
      }, 1400);
    }, 100);
  }

  shouldAnimate() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return false;
    }

    const isMobile = window.innerWidth < 750;
    return !isMobile;
  }

  setupScrollTracking() {
    if (this.scrollListener) return;

    // Capture initial position for spacer height
    const rect = this.container.getBoundingClientRect();
    this.initialRect = {
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height
    };

    let ticking = false;
    this.scrollListener = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', this.scrollListener, { passive: true });
  }

  handleScroll() {
    if (this.hasCompletedAnimation) return;

    const productInfoSection = this.container.closest('.product-information');
    const sectionRect = productInfoSection.getBoundingClientRect();

    // Wait until product section bottom crosses viewport bottom
    if (sectionRect.bottom > window.innerHeight) {
      // repositions image where it started  
      this.container.style.position = 'static';
      return;
    }

    // Initialize animation on first scroll past section
    if (!this.hasStartedScrollAnimation && !this.hasCompletedAnimation) {
      this.initializeScrollAnimation();
      if (!this.targetPosition) {
        return;
      }
    }

    // Update animation on every scroll
    if (this.hasStartedScrollAnimation && !this.hasCompletedAnimation) {
      this.updateAnimationProgress();
    }
  }

  initializeScrollAnimation() {
    this.hasStartedScrollAnimation = true;

    const currentRect = this.container.getBoundingClientRect();

    // Activate spacer to maintain layout
    this.spacerElement = this.container.previousElementSibling;
    if (this.spacerElement && this.spacerElement.classList.contains('product-media-animation-spacer')) {
      this.spacerElement.style.display = 'block';
      this.spacerElement.style.height = `${this.initialRect.height}px`;
    }

    // Switch to fixed positioning at current viewport position
    this.container.style.position = 'fixed';
    this.container.style.left = `${currentRect.left}px`;
    this.container.style.top = `${currentRect.top}px`;
    this.container.style.width = `${currentRect.width}px`;
    this.container.style.height = `${currentRect.height}px`;
    this.container.style.zIndex = '1000';
    this.container.setAttribute('data-animation-phase', 'scrolling');

    this.startPosition = {
      x: currentRect.left,
      y: currentRect.top,
      width: currentRect.width,
      height: currentRect.height
    };

    // Find and calculate target carousel position
    this.targetPosition = this.calculateCarouselPosition();

    if (!this.targetPosition) return;

    // Hide target carousel image
    if (this.targetCarouselImage) {
      this.targetCarouselImage.style.transition = 'none';
      this.targetCarouselImage.style.opacity = '0';
    }

    // Calculate scroll distance: animation completes when carousel image is at 40% from top
    const targetImageRect = this.targetCarouselImage.getBoundingClientRect();
    const targetViewportPosition = window.innerHeight * 0.4;
    this.scrollDistance = Math.max(400, targetImageRect.top - targetViewportPosition);

    this.scrollStartY = window.scrollY;
  }

  updateAnimationProgress() {
    if (this.hasCompletedAnimation) return;

    const currentScrollY = window.scrollY;
    const scrollDelta = currentScrollY - this.scrollStartY;

    // Calculate progress (0 to 1) based on scroll
    const progress = Math.min(scrollDelta / this.scrollDistance, 1);
    const easedProgress = this.easeInOutQuad(progress);

    // Get current target position (moves as user scrolls)
    const currentTargetRect = this.targetCarouselImage.getBoundingClientRect();

    // Interpolate from start to current target position
    const currentLeft = this.startPosition.x + (currentTargetRect.left - this.startPosition.x) * easedProgress;
    const currentTop = this.startPosition.y + (currentTargetRect.top - this.startPosition.y) * easedProgress;
    const currentWidth = this.startPosition.width + (currentTargetRect.width - this.startPosition.width) * easedProgress;
    const currentHeight = this.startPosition.height + (currentTargetRect.height - this.startPosition.height) * easedProgress;

    this.container.style.position = 'fixed';
    this.container.style.left = `${currentLeft}px`;
    this.container.style.top = `${currentTop}px`;
    this.container.style.width = `${currentWidth}px`;
    this.container.style.height = `${currentHeight}px`;

    // Complete animation at 100%
    if (progress >= 1) {
      this.container.style.left = `${currentTargetRect.left}px`;
      this.container.style.top = `${currentTargetRect.top}px`;
      this.container.style.width = `${currentTargetRect.width}px`;
      this.container.style.height = `${currentTargetRect.height}px`;
      this.completeAnimation();
    }
  }

  calculateCarouselPosition() {
    this.targetCarouselSlide = this.findTargetSlide();
    if (!this.targetCarouselSlide) return null;

    // Find image in slide
    this.targetCarouselImage = this.targetCarouselSlide.querySelector('.product-media img') ||
      this.targetCarouselSlide.querySelector('.card-gallery img') ||
      this.targetCarouselSlide.querySelector('.product-media__image') ||
      this.targetCarouselSlide.querySelector('img');

    if (!this.targetCarouselImage) return null;

    const imageRect = this.targetCarouselImage.getBoundingClientRect();

    return {
      x: imageRect.left,
      y: imageRect.top,
      width: imageRect.width,
      height: imageRect.height
    };
  }

  disableCarousel() {
    const carousel = this.findNearestCarousel();
    if (!carousel) return null;

    carousel.setAttribute('disabled', 'true');
  }

  enableCarousel() {
    const carousel = this.findNearestCarousel();
    if (!carousel) return null;

    carousel.setAttribute('disabled', 'false');

  }
  findTargetSlide() {
    const carousel = this.findNearestCarousel();
    if (!carousel) return null;

    // Find first visible slide
    const allSlides = carousel.querySelectorAll('slideshow-slide');
    return Array.from(allSlides).find(slide =>
      slide.getAttribute('aria-hidden') === 'false'
    );
  }

  findNearestCarousel() {
    const productInfoSection = this.container.closest('.shopify-section');

    if (productInfoSection) {
      const nextSection = productInfoSection.nextElementSibling;

      if (nextSection) {
        const carousel = nextSection.querySelector('.resource-list__carousel slideshow-component');
        if (carousel) return carousel;
      }
    }

    // Fallback: search entire document
    return document.querySelector('.resource-list__carousel slideshow-component');
  }

  completeAnimation() {
    this.hasCompletedAnimation = true;
    this.enableCarousel();

    // Swap images instantly
    if (this.targetCarouselImage && this.productImage) {
      this.targetCarouselImage.src = this.productImage.src;
      this.targetCarouselImage.alt = this.productImage.alt || 'Product image';

      if (this.targetCarouselImage.srcset) {
        this.targetCarouselImage.removeAttribute('srcset');
      }

      this.targetCarouselImage.style.transition = 'none';
      this.targetCarouselImage.style.opacity = '1';
    }

    // Hide animation container instantly
    // this.container.style.transition = 'none';
    // this.container.style.opacity = '0';
    // this.container.style.display = 'none';
    this.container.setAttribute('data-animation-phase', 'complete');
    this.container.removeAttribute('style');

    // Remove scroll listener
    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener);
      this.scrollListener = null;
    }
  }

  easeInOutQuad(t) {
    return t < 0.5
      ? 2 * t * t
      : 1 - Math.pow(-2 * t + 2, 2) / 2;
  }

  destroy() {
    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener);
      this.scrollListener = null;
    }
  }
}

// Global API for managing instances
window.ProductInformationAnimation = {
  instances: new Map(),

  init(container) {
    const containerId = container.dataset.mediaId || 'default';

    if (this.instances.has(containerId)) {
      this.instances.get(containerId).destroy();
    }

    const instance = new ProductInformationAnimation(container);
    this.instances.set(containerId, instance);
    instance.init();

    return instance;
  },

  destroy(containerId) {
    if (this.instances.has(containerId)) {
      this.instances.get(containerId).destroy();
      this.instances.delete(containerId);
    }
  },

  destroyAll() {
    this.instances.forEach(instance => instance.destroy());
    this.instances.clear();
  }
};

// Auto-initialize on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const containers = document.querySelectorAll('.product-media-animation-container[data-animation-enabled="true"]');
    containers.forEach(container => window.ProductInformationAnimation.init(container));
  });
} else {
  const containers = document.querySelectorAll('.product-media-animation-container[data-animation-enabled="true"]');
  containers.forEach(container => window.ProductInformationAnimation.init(container));
}

// Shopify theme editor support
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:unload', () => {
    window.ProductInformationAnimation.destroyAll();
  });

  document.addEventListener('shopify:section:load', () => {
    const containers = document.querySelectorAll('.product-media-animation-container[data-animation-enabled="true"]');
    containers.forEach(container => window.ProductInformationAnimation.init(container));
  });
}
